/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-sparse-arrays */
import React, { useState, useEffect } from "react";
import styles from "./AccessSubPages.module.css";
import { AccessSubPageShell, useScrollToTop } from "./AccessSubPageShell";
import { AnalyticsService } from "../../analytics/AnalyticsService";

/* ============================================================================
   AccessWhatWeAskFor.jsx
   Medicare ACCESS Google Ads subpage — "What We Ask For"

   Content is the Medicare requirements section (S6) of the ACCESS landing
   page, lbpNewLandingPage.jsx, carried verbatim, including the state picker,
   the problem-area chips, the Medicare card illustration and the "What we
   will never ask for" block.

   One copy change from the LBP landing page: the problem-area note drops its
   trailing clause, per the general ACCESS copy spec, Section 3.6. That
   change applies to the live LBP landing page too.

   Chrome, CTA band and footer come from AccessSubPageShell.
   ============================================================================ */

/* ----------------------------------------------------------------------------
   SERVED STATES

   DEV NOTE — launch is these six states, per Arjun. The SERVED_STATES
   constant in lbpNewLandingPage.jsx (line 20) currently lists nine — it also
   includes NC, SC and WV — and needs to be reduced to match. The general
   ACCESS copy spec and the Google Ads subpages handoff both still document
   nine and need the same correction.
   ---------------------------------------------------------------------------- */
const SERVED_STATES = [
  ["DE", "Delaware"],
  ["DC", "District of Columbia"],
  ["MD", "Maryland"],
  ["NJ", "New Jersey"],
  ["PA", "Pennsylvania"],
  ["VA", "Virginia"],
];

const stateName = (ab) => (SERVED_STATES.find((s) => s[0] === ab) || [, ab])[1];

const AREA_LABELS = { LBP: "Lower back", HIP: "Hip", THIGH: "Thigh", KNEE: "Knee" };
const AREA_ORDER = ["LBP", "HIP", "THIGH", "KNEE"];

export const AccessWhatWeAskFor = () => {
  useScrollToTop();

  const [lpStateChoice, setLpStateChoice] = useState("");
  const [lpStateAck, setLpStateAck] = useState({ show: false, good: false, text: "" });
  const [lpAreaChoice, setLpAreaChoice] = useState("");

  /* DEV NOTE — analytics is not wired here on purpose. See the note in
     AccessAboutProgram.jsx; the same three open items apply. */
  useEffect(() => {
    // Track page view for the What We Ask For subpage
    const trackingFields = {
      variant: new URLSearchParams(window.location.search).get("variant") || localStorage.getItem("variant") || null,
      isComeFromGoogle: !!(new URLSearchParams(window.location.search).get("gclid") || localStorage.getItem("variant")),
      visitedAccessWhatWeAskFor: true,
      access_subpage: "what_we_ask_for",
    };

    AnalyticsService.startSession(trackingFields);
    AnalyticsService.trackStep({
      ...trackingFields,
      session_last_update: new Date().toISOString(),
    });
  }, []);

  /* DEV NOTE — this mirrors handleLpStateChange in lbpNewLandingPage.jsx,
     with one difference: the acknowledgment below actually renders. On the
     shipped landing page .stateack is display:none in CSS and the JSX never
     adds the .on class that would show it, so the visitor picks a state and
     sees nothing. The same fix is needed there. */
  const handleLpStateChange = (e) => {
    const v = e.target.value;
    setLpStateChoice(v === "__other" ? "" : v);
    if (!v) {
      setLpStateAck({ show: false, good: false, text: "" });
    } else if (v === "__other") {
      setLpStateAck({
        show: true,
        good: false,
        text: "We are not there yet. We are adding states over time.",
      });
    } else {
      setLpStateAck({
        show: true,
        good: true,
        text: `Good news — we serve ${stateName(v)}. You can check if you qualify.`,
      });
    }
  };
  /* DEV NOTE — a second click on the selected chip clears it. The shipped
     handleLpAreaPick in lbpNewLandingPage.jsx only sets the value and cannot
     clear it, so the visitor is locked into their first pick. The same fix
     is needed there. */
  const handleLpAreaPick = (code) => {
    setLpAreaChoice((prev) => (prev === code ? "" : code));
  };

  // Save preferences to localStorage (matching lbpNewLandingPage pattern).

  const savePreferences = () => {
    let existing = {};
    try {
      existing = JSON.parse(localStorage.getItem("virdio_lbp_preferences") || "{}");
    } catch (e) {
      existing = {};
    }
    localStorage.setItem("virdio_lbp_preferences", JSON.stringify({ ...existing, lpStateChoice, lpAreaChoice }));
  };

  useEffect(() => {
    if (lpStateChoice || lpAreaChoice) {
      savePreferences();
    }
  }, [lpStateChoice, lpAreaChoice]);

  /* DEV NOTE — the state and area picks above are local to this page. If the
     CTA is meant to carry them into the eligibility flow on the landing page,
     that is a wiring item: pass them through the same mechanism the landing
     page uses (lpStateChoice / lpAreaChoice in virdio_lbp_preferences, or a
     URL parameter). Nothing here writes to storage, so no key is assumed. */

  return (
    <AccessSubPageShell activePage="ask">
      {/* ===== HERO ===== */}
      <div className={styles.subhero}>
        <div className={styles.container}>
          <span className={styles.label}>Before you begin</span>
          <h1>What we will ask for, and what we never will.</h1>
          <p>Medicare decides who qualifies, not us. Here is exactly what we need in order to check on your behalf, and what no one from Virdio Health will ever ask you for.</p>
        </div>
      </div>

      {/* ===== BODY — landing page S6 ===== */}
      <section className={styles.ask}>
        <div className={styles.container}>
          <div className={styles.askGrid}>
            <div>
              <div className={styles.statebox}>
                <p>
                  First we will ask <b>which state you live in</b>, so we can confirm we have a physical therapist licensed to treat you where you live. You can check that right now.
                </p>
                <div className={styles.statepick}>
                  <select value={lpStateChoice} onChange={handleLpStateChange} aria-label="Select your state">
                    <option value="">Select your state</option>
                    {SERVED_STATES.map(([ab, nm]) => (
                      <option key={ab} value={ab}>
                        {nm}
                      </option>
                    ))}
                    <option value="__other">My state is not listed</option>
                  </select>
                </div>
                {lpStateAck.show && <div className={`${styles.stateack} ${lpStateAck.good ? styles.good : styles.bad}`}>{lpStateAck.text}</div>}

                <div className={styles.areacheck}>
                  <p className={styles.arealabel}>Problem areas we currently work with</p>
                  <div className={styles.arearow}>
                    {AREA_ORDER.map((code) => (
                      <button key={code} type="button" className={`${styles.areachip} ${lpAreaChoice === code ? styles.sel : ""}`} aria-pressed={lpAreaChoice === code} onClick={() => handleLpAreaPick(code)}>
                        {AREA_LABELS[code]}
                      </button>
                    ))}
                  </div>
                  <p className={styles.areanote}>Every state we serve covers all four. Pick the one that fits you and we will carry it through.</p>
                </div>
              </div>

              <p>After that, Medicare decides who qualifies, not us. To check on your behalf, Medicare needs four things:</p>

              <ul className={styles.askList}>
                <li>
                  <b>Your legal name</b>
                  <span>As it appears on your Medicare card</span>
                </li>
                <li>
                  <b>Your date of birth</b>
                  <span>This is not printed on your Medicare card. Medicare keeps it in its own records, and what we send has to match.</span>
                </li>
                <li>
                  <b>Your sex, as Medicare has it on record</b>
                  <span>Also not printed on your card, and also kept in Medicare's own records.</span>
                </li>
                <li>
                  <b>Your Medicare number</b>
                  <span>The 11-character number on the front of your red, white, and blue card</span>
                </li>
              </ul>

              <p className={styles.closer}>That is everything. Nothing else is asked for at any point.</p>
            </div>

            <figure className={styles.cardfig}>
              <svg viewBox="0 0 400 232" width="100%" role="img" aria-label="Illustration showing where the 11-character Medicare number appears on a Medicare card">
                <rect x="6" y="6" width="388" height="220" rx="14" fill="#ffffff" stroke="#cbd5e1" strokeWidth="2" />
                <rect x="6" y="6" width="388" height="30" rx="14" fill="#e2e8f0" />
                <rect x="6" y="26" width="388" height="10" fill="#e2e8f0" />
                <rect x="26" y="16" width="120" height="10" rx="5" fill="#94a3b8" />
                <rect x="26" y="58" width="70" height="9" rx="4.5" fill="#cbd5e1" />
                <rect x="26" y="76" width="150" height="13" rx="6" fill="#94a3b8" />
                <rect x="26" y="112" width="130" height="9" rx="4.5" fill="#cbd5e1" />
                <rect x="20" y="128" width="250" height="44" rx="8" fill="#e6f7f6" stroke="#0d9488" strokeWidth="2.5" />
                <text x="34" y="158" fontFamily="ui-monospace, Menlo, monospace" fontSize="23" letterSpacing="2.5" fill="#0f172a" fontWeight="700">
                  1EG4-TE5-MK73
                </text>
                <text x="286" y="152" fontFamily="DM Sans, sans-serif" fontSize="13" fill="#0d9488" fontWeight="700">
                  This one
                </text>
                <path d="M282 147 L272 150" stroke="#0d9488" strokeWidth="2.5" strokeLinecap="round" />
                <rect x="26" y="190" width="90" height="9" rx="4.5" fill="#cbd5e1" />
                <rect x="130" y="190" width="90" height="9" rx="4.5" fill="#cbd5e1" />
              </svg>
              <figcaption>
                Your Medicare number is 11 characters, made up of numbers and capital letters. The dashes on the card are only there to make it easier to read. You can type it with or without them.
                <br />
                <br />
                <span className={styles.ph}>Illustration only — not a reproduction of the Medicare card</span>
              </figcaption>
            </figure>
          </div>

          <div className={styles.never}>
            <h3>What we will never ask for</h3>
            <ul>
              <li>We will never ask for a credit card.</li>
              <li>We will never ask for your bank account.</li>
              <li>We will never ask for your Social Security number.</li>
              <li>We will never ask for a payment of any kind.</li>
            </ul>
            <p>Checking whether you qualify is free, and if you qualify the Medicare program costs you nothing — there is nothing to buy and nothing to cancel. If you do not qualify, we will tell you plainly and show you what else is available. Nothing is ever charged unless you choose it first. If anyone claiming to be from Virdio Health asks you for money or payment details to check your eligibility, it is not us.</p>
          </div>
        </div>
      </section>
    </AccessSubPageShell>
  );
};

export default AccessWhatWeAskFor;
